import React, { useState } from 'react';
import { TextField, CircularProgress } from '@material-ui/core';
import { useAuth } from '@context/auth';
import { Button } from '@components/Button';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import {
  Card,
  ContainerButtons,
  ContainerSignIn,
  Form,
  TitleSignIn,
  LoadingOverlay,
  StyledPaper,
  LogoContainer,
  InputContainer,
  ErrorText,
  LoadingContent,
  LoadingText,
  SubText,
  FooterText,
  BackgroundPattern
} from './styles';
import backgroundImage from '../../assets/prancheta2.png';

const signInForm = z.object({
  email: z.string().email({ message: 'Por favor, insira um e-mail válido' }),
  password: z.string().min(4, { message: 'A senha deve ter pelo menos 4 caracteres' })
});

type SignForm = z.infer<typeof signInForm>;

export default function SignIn() {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SignForm>();

  const { signIn, messageError } = useAuth();

  async function handleLogin(data: SignForm) {
    const { email, password } = data;
    setIsLoading(true);
    
    try {
      await signIn(email, password);
    } catch (error) {
      console.error('Erro no login:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {isLoading && (
        <LoadingOverlay>
          <LoadingContent>
            <CircularProgress size={60} style={{ color: '#ffffff' }} />
            <LoadingText>Acessando o Sistema</LoadingText>
            <SubText>Estamos conectando você à plataforma</SubText>
          </LoadingContent>
        </LoadingOverlay>
      )}
      
      <ContainerSignIn>
        <BackgroundPattern />
        <Card>
          <StyledPaper elevation={8}>
            <LogoContainer>
              <img 
                src={backgroundImage} 
                height={80} 
                width={160} 
                alt="Logo Aryz" 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                }}
              />
            </LogoContainer>
            
            <TitleSignIn>
              Portal Aryz
            </TitleSignIn>
            
            <Form onSubmit={handleSubmit(handleLogin)}>
              <InputContainer>
                <TextField
                  type="email"
                  id="email"
                  fullWidth
                  label="E-mail corporativo"
                  variant="outlined"
                  margin="normal"
                  disabled={isLoading}
                  error={!!errors.email}
                  {...register('email')}
                />
                <ErrorText>
                  {errors.email?.message}
                </ErrorText>
              </InputContainer>

              <InputContainer>
                <TextField
                  type="password"
                  id="password"
                  fullWidth
                  label="Senha de acesso"
                  variant="outlined"
                  margin="normal"
                  disabled={isLoading}
                  error={!!errors.password}
                  {...register('password')}
                />
                <ErrorText>
                  {errors.password?.message || messageError}
                </ErrorText>
              </InputContainer>

              <ContainerButtons>
                <Button
                  title={isLoading ? "Conectando..." : "Acessar Sistema"}
                  variantstyle={isLoading ? 'DEFAULT' : 'PURPLE'}
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                  style={{
                    minWidth: '220px',
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '10px'
                  }}
                />
              </ContainerButtons>
            </Form>

            <FooterText>
              © 2024 Aryz • Sistema de Gestão Integrada
            </FooterText>
          </StyledPaper>
        </Card>
      </ContainerSignIn>
    </>
  );
}